
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { TemplateList } from './TemplateList';
import { EditTemplateForm } from './EditTemplateForm';

interface Template {
  id: string;
  title: string;
  description: string | null;
  hebrew_title: string | null;
  hebrew_description: string | null;
}

interface TemplateManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const TemplateManagementDialog = ({ 
  open, 
  onOpenChange 
}: TemplateManagementDialogProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['taskTemplates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('task_templates')
        .select('*');

      if (error) throw error;
      return data as Template[];
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: async (templateId: string) => {
      const { error } = await supabase
        .from('task_templates')
        .delete()
        .eq('id', templateId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taskTemplates'] });
      toast({
        title: "Template deleted",
        description: "The template has been successfully deleted.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete template. Please try again.",
        variant: "destructive",
      });
    },
  });

  const editTemplateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Template> }) => {
      const { error } = await supabase
        .from('task_templates')
        .update({
          title: data.title,
          description: data.description,
          hebrew_title: data.hebrew_title,
          hebrew_description: data.hebrew_description,
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taskTemplates'] });
      setEditingTemplate(null);
      toast({
        title: "Template updated",
        description: "The template has been successfully updated.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update template. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleEditSubmit = (data: any) => {
    if (editingTemplate) {
      editTemplateMutation.mutate({
        id: editingTemplate.id,
        data: {
          title: data.title,
          description: data.description,
          hebrew_title: data.hebrewTitle,
          hebrew_description: data.hebrewDescription,
        },
      });
    }
  };

  if (editingTemplate) {
    return (
      <EditTemplateForm
        template={editingTemplate}
        open={open}
        onOpenChange={(newOpen) => {
          if (!newOpen) {
            setEditingTemplate(null);
          }
          onOpenChange(newOpen);
        }}
        onSubmit={handleEditSubmit}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]" dir="rtl">
        <DialogHeader>
          <DialogTitle className="text-right">ניהול תבניות משימות</DialogTitle>
        </DialogHeader>
        <TemplateList
          templates={templates}
          isLoading={isLoading}
          onEdit={setEditingTemplate}
          onDelete={(templateId) => deleteTemplateMutation.mutate(templateId)}
        />
      </DialogContent>
    </Dialog>
  );
};
